import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { PostRegInterestParams } from '@base/models/requests/request-interest.req';
import { ImageList, Interest, RequestInterest } from '@base/models/responses/request-interest.res';
import { MeetService } from '@base/services/private/meet.service';
import { ModalService } from '@base/services/private/modal.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { YourInterestsComponent } from '@base/components/private//others/your-interests/your-interests.component';
import { IdParams } from '@base/models/requests/requests.req';
import { SkeletonGeneralListComponent } from '@base/components/private/skeleton/skeleton-general-list/skeleton-general-list.component';
import { GlobalService } from '@base/services/private/global.service';
import { Subscription, tap } from 'rxjs';
import { StatusResponse } from '@base/enums/status-response.enum';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { GlobalStateService } from '@base/services/private/global-state.service';

@Component({
  selector: 'app-modal-accept-request-success',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    YourInterestsComponent,
    SkeletonGeneralListComponent,
    ModalFooterComponent,
    forwardRef(() => TranslatePipe),
  ],
  providers: [forwardRef(() => TranslatePipe)],
  templateUrl: './modal-accept-request-success.component.html',
  styleUrl: './modal-accept-request-success.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalAcceptRequestSuccessComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly meetSrv = inject(MeetService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalSrv = inject(GlobalService);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly translatePipe = inject(TranslatePipe);

  @Input() heading = 'CONGRATULATIONS!';
  @Input() body = 'YOU_CAN_NOW_DISCUSS_AND_CHAT_WITH_USER_ON_MESSAGES';
  @Input() name = '';
  @Input() peopleId = '';
  @Input() modalId = '';
  @Input() titleForMiniApp = '';

  @Input() title = 'TO_IMPROVE_THE_FOCUS_OF_THIS_MEETING_PLEASE_SELECT_YOUR_INTERESTS';
  interestsSelected: Interest[] = [];
  productsSelected: ImageList[] = [];
  interests: RequestInterest | null = null;

  isShowPlaceholder = true;
  isHttpRequestRunning = false;

  httpRequestObserver$: Subscription | null = null;
  isExternalMode = this.globalStateSrv.isExternalMode();

  ngOnInit(): void {
    this.listenHttpRequestState();
    this.getInterest();
  }

  ngOnDestroy(): void {
    this.httpRequestObserver$?.unsubscribe();
    this.isHttpRequestRunning = false;
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Listens for HTTP request state changes and updates the component state accordingly.
   * @returns void
   */
  listenHttpRequestState(): void {
    this.httpRequestObserver$ = this.globalSrv.httpRequestObserver().subscribe({
      next: (res) => {
        this.isHttpRequestRunning = res;
        this.triggerChangeDetection();
      },
    });
  }

  /**
   * Closes the modal with the given modalId.
   * @returns void
   */
  close(): void {
    this.modalSrv.close();
  }

  /**
   * Shows a modal warning if the account is a preview account and status response is -2.
   */
  showModalWarning(): void {
    this.modalSrv.showModalWarningForPreviewAccount();
    return;
  }

  /**
   * Sends the selected interests and products to the server for the current person.
   * Closes the modal or all modals based on the closeAll flag after successful response.
   * @returns void
   */
  sentUserInterest(): void {
    const params: PostRegInterestParams = {
      id: this.peopleId,
      data: {
        interest: this.interestsSelected,
        image_list: this.productsSelected,
      },
    };
    this.meetSrv.postReqInterest(params).subscribe({
      next: (res) => {
        if (res.status === StatusResponse.SUCCESS) {
          this.close();
          // handle external mode or cancel from sense
          if (this.isExternalMode) {
            this.globalSrv.postMessageToTargets({
              is_success: true,
              message: this.translatePipe.transform(this.titleForMiniApp),
              description: this.translatePipe.transform('CLOSE_WINDOW'),
              isShowModal: true,
            });
            return;
          }
        } else if (+res.status === StatusResponse.PREVIEW_ACCOUNT) {
          // Preview account returns status -2, but success returns 'SUCCESS'; always convert to number to check for -2.
          this.showModalWarning();
        }
      },
      error: (err) => {
        console.error('Error sending user interests:', err);
      },
    });
  }

  /**
   * Updates the selected interests and products based on user input.
   * @param event - The event object containing updated interests and products.
   * @returns void
   */
  changeInterest(event: { interests: Interest[]; products: ImageList[] }): void {
    const { interests, products } = event;
    this.interestsSelected = interests;
    this.productsSelected = products;
  }

  /**
   * Fetches interests and products for the given peopleId.
   * Updates interests and products state.
   * @returns void
   */
  getInterest(): void {
    const params: IdParams = {
      id: this.peopleId,
    };
    this.meetSrv
      .getReqInterest(params)
      .pipe(
        tap(() => {
          this.isShowPlaceholder = true;
        })
      )
      .subscribe({
        next: (res) => {
          this.interests = res;
          this.interestsSelected = res.interest;
          this.productsSelected = res.image_list;
          this.isShowPlaceholder = false;
          this.triggerChangeDetection();
        },
        error: (err) => {
          console.error('Error fetching interests:', err);
          this.isShowPlaceholder = false;
        },
      });
  }

  /**
   * Triggers change detection for the component.
   */
  triggerChangeDetection(): void {
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }
}
